import { useMemo } from 'react'

import { Option } from '@joor/design-system'

import { formatNameWithCode } from 'utils/formatters'
import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { useOrderDetailsEditInfo } from './orderDetailsForm.queries'

const FAKE_EVENT_TYPE_STR = 'FakeEvent'

export const generateYearOptions = () => {
  const actualYear = new Date().getFullYear()

  return Array.from({ length: 21 }, (_, i) => {
    const year = actualYear - 10 + i
    return {
      value: year.toString(),
      label: year.toString(),
    }
  })
}

export const useOrderDetailsDropdownOptions = ({
  brandId,
  retailerId,
  injectableOrderEvent,
  isBrand,
}: {
  brandId: string
  retailerId: string
  injectableOrderEvent?: { value: string | null; label: string } | null
  isBrand: boolean
}) => {
  const { data, loading } = useOrderDetailsEditInfo({
    brandId,
    retailerId,
    isBrand,
  })

  const brand = useMemo(() => data?.brands?.edges[0]?.node, [data])

  const shouldAppendEventToOptions =
    fromGlobalId(injectableOrderEvent?.value).type === FAKE_EVENT_TYPE_STR

  const eventOptionToInject =
    injectableOrderEvent && shouldAppendEventToOptions
      ? [injectableOrderEvent]
      : []

  const brandsOrderEventsOptions: Option[] = useMemo(
    () =>
      brand?.orderEvents?.map(({ name, id, code }) =>
        // Needed bc of a missmatch between 'LibraryValue' (brands) and 'OrderEvent' (order)
        ({
          value: toGlobalId('OrderEvent', fromGlobalId(id).id) ?? '',
          label: formatNameWithCode(name, code),
        }),
      ) ?? [],
    [brand],
  )

  const orderTypesOptions = useMemo(
    () =>
      brand?.orderTypes?.edges?.map(({ node }) => ({
        value: node?.id,
        label: formatNameWithCode(node?.name, node?.code),
      })) ?? [],
    [brand],
  )

  const listConnectionWarehousesIds = useMemo(
    () =>
      data?.listConnectionWarehouses?.map(({ warehouseId }) => warehouseId) ??
      [],
    [data],
  )

  const brandWarehouses = useMemo(
    () => brand?.warehouses?.edges.map((edge) => edge?.node) ?? [],
    [brand],
  )

  const warehousesDataNodes = useMemo(
    () =>
      Boolean(listConnectionWarehousesIds.length)
        ? brandWarehouses.filter(
            (node) => node?.id && listConnectionWarehousesIds.includes(node.id),
          )
        : brandWarehouses,
    [brandWarehouses, listConnectionWarehousesIds],
  )

  const warehouseOptions = useMemo(
    () =>
      warehousesDataNodes?.map((node) => ({
        value: node?.id,
        label: formatNameWithCode(node?.name, node?.code),
      })) ?? [],

    [warehousesDataNodes],
  )

  const seasonsOptions: Option[] = useMemo(
    () =>
      brand?.seasons.edges.map(({ node }) => ({
        value: node?.id,
        label: node?.name,
      })) ?? [],
    [brand],
  )

  const yearOptions: Option[] = generateYearOptions()

  return {
    loadingOptions: loading,
    orderEventsOptions: [...eventOptionToInject, ...brandsOrderEventsOptions],
    orderTypesOptions,
    warehouseOptions,
    seasonsOptions,
    yearOptions,
  }
}
